import { BookOpen, Building2, UserRound, Users } from 'lucide-react';
import { fetchCustomersListPage } from 'queries/customersListQueries';
import { fetchSuppliersListPage } from 'queries/suppliersListQueries';
import type Category from 'types/category';
import type {
  CommandPaletteDirectedSearchResult,
  ParsedCommandPaletteDirectedQuery,
} from 'types/commandPaletteDirectedSearch';
import { COMMAND_PALETTE_DIRECTED_URL_KEYS } from 'types/commandPaletteDirectedSearch';
import { flattenCategories } from 'utils/categoryUtils';
import {
  scoreCategoryDirectSearch,
  sortCategoriesForFlatPicker,
} from 'utils/categorySearchRank';
import { API_ROUTES, ROUTES } from 'utils/constants';
import { getThisMonthRange } from 'utils/dateUtils';
import { clientAuthorizedGet } from 'utils/fetchUtils';
import { translate } from 'utils/i18nTranslate';

const DIRECTED_SEARCH_RESULT_LIMIT = 5;

type CompanyUserListItem = {
  id: string;
  email: string;
  username?: string;
};

function buildGeneralLedgerPath(categoryId: string): string {
  const { dateFrom, dateTo } = getThisMonthRange();
  const params = new URLSearchParams({
    categoryId,
    dateFrom,
    dateTo,
  });
  return `${ROUTES.REPORTS.GENERAL_LEDGER}?${params.toString()}`;
}

function buildCustomersListPath(search: string): string {
  const params = new URLSearchParams({
    [COMMAND_PALETTE_DIRECTED_URL_KEYS.LIST_SEARCH]: search,
  });
  return `${ROUTES.JOURNALS.CUSTOMERS}?${params.toString()}`;
}

function buildCustomerViewPath(customerId: string): string {
  const params = new URLSearchParams({
    [COMMAND_PALETTE_DIRECTED_URL_KEYS.VIEW]: customerId,
  });
  return `${ROUTES.JOURNALS.CUSTOMERS}?${params.toString()}`;
}

function buildSuppliersListPath(search: string): string {
  const params = new URLSearchParams({
    [COMMAND_PALETTE_DIRECTED_URL_KEYS.LIST_SEARCH]: search,
  });
  return `${ROUTES.JOURNALS.SUPPLIERS}?${params.toString()}`;
}

function buildSupplierViewPath(supplierId: string): string {
  const params = new URLSearchParams({
    [COMMAND_PALETTE_DIRECTED_URL_KEYS.VIEW]: supplierId,
  });
  return `${ROUTES.JOURNALS.SUPPLIERS}?${params.toString()}`;
}

function buildCompanyUserPath(userId: string): string {
  const params = new URLSearchParams({
    [COMMAND_PALETTE_DIRECTED_URL_KEYS.HIGHLIGHT_USER]: userId,
  });
  return `${ROUTES.SETTINGS.USERS}?${params.toString()}`;
}

function emailsMatch(storedEmail: string, queryEmail: string): boolean {
  return storedEmail.trim().toLowerCase() === queryEmail.trim().toLowerCase();
}

export function searchGeneralLedgerAccounts(
  searchText: string,
  categories: Record<string, Category>,
): CommandPaletteDirectedSearchResult[] {
  const flatCategories = flattenCategories(categories).map(
    (entry) => entry.category,
  );
  const sorted = sortCategoriesForFlatPicker(
    flatCategories,
    searchText,
    categories,
  );

  return sorted
    .filter((category) => scoreCategoryDirectSearch(searchText, category) > 0)
    .slice(0, DIRECTED_SEARCH_RESULT_LIMIT)
    .map((category) => ({
      id: `general_ledger_account:${category.id}`,
      kind: 'general_ledger_account',
      title: category.name,
      subtitle: category.accountNumber?.trim() || null,
      path: buildGeneralLedgerPath(category.id),
      icon: BookOpen,
      sectionKey: 'commandPalette.sections.accounts',
    }));
}

async function searchCustomersByName(
  companyId: string,
  searchText: string,
): Promise<CommandPaletteDirectedSearchResult[]> {
  const page = await fetchCustomersListPage(companyId, {
    page: 1,
    limit: DIRECTED_SEARCH_RESULT_LIMIT,
    search: searchText,
    includeVoided: false,
    includeGroupIds: '',
    excludeGroupIds: '',
  });

  const results: CommandPaletteDirectedSearchResult[] = page.data.map(
    (customer) => ({
      id: `customer:${customer.id}`,
      kind: 'customer',
      title: customer.name,
      subtitle: customer.email?.trim() || null,
      path: buildCustomerViewPath(customer.id),
      icon: Users,
      sectionKey: 'commandPalette.sections.contacts',
    }),
  );

  if (page.meta.totalItems > results.length) {
    results.push({
      id: 'customer:list',
      kind: 'customer_list',
      title: translate('commandPalette.directed.viewAllCustomers', {
        query: searchText,
      }),
      subtitle: null,
      path: buildCustomersListPath(searchText),
      icon: Users,
      sectionKey: 'commandPalette.sections.contacts',
    });
  }

  return results;
}

async function searchSuppliersByName(
  companyId: string,
  searchText: string,
): Promise<CommandPaletteDirectedSearchResult[]> {
  const page = await fetchSuppliersListPage(companyId, {
    page: 1,
    limit: DIRECTED_SEARCH_RESULT_LIMIT,
    search: searchText,
    includeVoided: false,
    includeGroupIds: '',
    excludeGroupIds: '',
  });

  const results: CommandPaletteDirectedSearchResult[] = page.data.map(
    (supplier) => ({
      id: `supplier:${supplier.id}`,
      kind: 'supplier',
      title: supplier.name,
      subtitle: supplier.email?.trim() || null,
      path: buildSupplierViewPath(supplier.id),
      icon: Building2,
      sectionKey: 'commandPalette.sections.contacts',
    }),
  );

  if (page.meta.totalItems > results.length) {
    results.push({
      id: 'supplier:list',
      kind: 'supplier_list',
      title: translate('commandPalette.directed.viewAllSuppliers', {
        query: searchText,
      }),
      subtitle: null,
      path: buildSuppliersListPath(searchText),
      icon: Building2,
      sectionKey: 'commandPalette.sections.contacts',
    });
  }

  return results;
}

async function searchContactsByEmail(
  companyId: string,
  email: string,
  canAccessPath: (path: string) => boolean,
): Promise<CommandPaletteDirectedSearchResult[]> {
  const results: CommandPaletteDirectedSearchResult[] = [];

  if (canAccessPath(ROUTES.SETTINGS.USERS)) {
    try {
      const users = await clientAuthorizedGet<CompanyUserListItem[]>(
        `${API_ROUTES.COMPANY}/${companyId}/users`,
        {},
        companyId,
      );

      for (const user of users) {
        if (emailsMatch(user.email, email)) {
          results.push({
            id: `company_user:${user.id}`,
            kind: 'company_user',
            title: user.username?.trim() || user.email,
            subtitle: user.email,
            path: buildCompanyUserPath(user.id),
            icon: UserRound,
            sectionKey: 'commandPalette.sections.emailMatches',
          });
        }
      }
    } catch {
      /* owner-only list may fail for members; keep searching contacts */
    }
  }

  if (
    canAccessPath(ROUTES.JOURNALS.CUSTOMERS) &&
    results.length < DIRECTED_SEARCH_RESULT_LIMIT
  ) {
    try {
      const customersPage = await fetchCustomersListPage(companyId, {
        page: 1,
        limit: DIRECTED_SEARCH_RESULT_LIMIT,
        search: email,
        includeVoided: false,
        includeGroupIds: '',
        excludeGroupIds: '',
      });

      for (const customer of customersPage.data) {
        if (!customer.email || !emailsMatch(customer.email, email)) {
          continue;
        }

        results.push({
          id: `customer:${customer.id}`,
          kind: 'customer',
          title: customer.name,
          subtitle: customer.email.trim(),
          path: buildCustomerViewPath(customer.id),
          icon: Users,
          sectionKey: 'commandPalette.sections.emailMatches',
        });

        if (results.length >= DIRECTED_SEARCH_RESULT_LIMIT) {
          return results;
        }
      }
    } catch {
      /* continue to suppliers when customer lookup fails */
    }
  }

  if (
    canAccessPath(ROUTES.JOURNALS.SUPPLIERS) &&
    results.length < DIRECTED_SEARCH_RESULT_LIMIT
  ) {
    try {
      const suppliersPage = await fetchSuppliersListPage(companyId, {
        page: 1,
        limit: DIRECTED_SEARCH_RESULT_LIMIT,
        search: email,
        includeVoided: false,
        includeGroupIds: '',
        excludeGroupIds: '',
      });

      for (const supplier of suppliersPage.data) {
        if (!supplier.email || !emailsMatch(supplier.email, email)) {
          continue;
        }

        results.push({
          id: `supplier:${supplier.id}`,
          kind: 'supplier',
          title: supplier.name,
          subtitle: supplier.email.trim(),
          path: buildSupplierViewPath(supplier.id),
          icon: Building2,
          sectionKey: 'commandPalette.sections.emailMatches',
        });

        if (results.length >= DIRECTED_SEARCH_RESULT_LIMIT) {
          break;
        }
      }
    } catch {
      /* return partial matches from earlier sources */
    }
  }

  return results.slice(0, DIRECTED_SEARCH_RESULT_LIMIT);
}

export async function searchCommandPaletteDirected(
  parsedQuery: ParsedCommandPaletteDirectedQuery,
  context: {
    companyId: string;
    categories: Record<string, Category>;
    canAccessPath: (path: string) => boolean;
  },
): Promise<CommandPaletteDirectedSearchResult[]> {
  switch (parsedQuery.mode) {
    case 'general_ledger': {
      if (!context.canAccessPath(ROUTES.REPORTS.GENERAL_LEDGER)) {
        return [];
      }
      return searchGeneralLedgerAccounts(
        parsedQuery.searchText,
        context.categories,
      );
    }
    case 'customer': {
      if (!context.canAccessPath(ROUTES.JOURNALS.CUSTOMERS)) {
        return [];
      }
      return searchCustomersByName(context.companyId, parsedQuery.searchText);
    }
    case 'supplier': {
      if (!context.canAccessPath(ROUTES.JOURNALS.SUPPLIERS)) {
        return [];
      }
      return searchSuppliersByName(context.companyId, parsedQuery.searchText);
    }
    case 'email':
      return searchContactsByEmail(
        context.companyId,
        parsedQuery.email,
        context.canAccessPath,
      );
    default:
      return [];
  }
}
